import { useRef, useState } from "react";
import { apiErrorMessage } from "../lib/api";
import { IMAGE_ACCEPT, formatBytes, uploadImage, validateImageFile } from "../lib/imageUpload";
import { Button, cx, Input, Spinner, UploadIcon, XIcon } from "./ui";

/**
 * An image as an admin actually has it: a file on their laptop. Uploading fills
 * in the URL the surrounding form already knows how to save — no record is
 * written here, so an upload the admin abandons leaves things exactly as they
 * were.
 *
 * Pasting a link stays available under the picker. Banners and logos created
 * before uploads existed are external URLs, and taking that away would break
 * them.
 *
 * Shared by the tournament banner (issue #22) and the site logo. What differs
 * between them is the presign endpoint, the size cap, the preview shape, and
 * the copy; the drag/upload/error state machine is the same, and a second copy
 * of it is where the two stop behaving alike.
 */

/** `wide` is a hero crop; `square` is a mark on a tile, whole and uncropped. */
const previews = {
  wide: "h-32 w-full rounded-xl border border-slate-200 bg-slate-50 object-cover",
  square: "h-24 w-24 rounded-xl border border-slate-200 bg-white object-contain p-2",
} as const;

export function ImageUploadField({
  label,
  hint,
  endpoint,
  maxBytes,
  value,
  onChange,
  shape = "wide",
  saveHint,
  linkPlaceholder = "https://example.com/image.jpg",
}: {
  label: string;
  hint?: string;
  /** Presign route this field POSTs to. See `lib/imageUpload`. */
  endpoint: string;
  maxBytes: number;
  value: string;
  onChange: (url: string) => void;
  shape?: keyof typeof previews;
  /** What the admin must still do for the upload to take effect, e.g. "Save branding to apply it." */
  saveHint: string;
  linkPlaceholder?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const take = async (file: File | undefined) => {
    if (!file) return;
    // Checked here so a wrong file costs a message instead of a round trip;
    // the API validates the same things, since this is not the boundary.
    const problem = validateImageFile(file, maxBytes);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    setUploaded(false);
    setUploading(true);
    try {
      onChange(await uploadImage(endpoint, file));
      setUploaded(true);
    } catch (e) {
      setError(apiErrorMessage(e, "That image didn't upload. Try again."));
    } finally {
      setUploading(false);
    }
  };

  const choose = () => fileRef.current?.click();

  return (
    <div className="space-y-2">
      <div>
        <span className="block text-sm font-medium text-slate-600">{label}</span>
        {hint ? <span className="mt-0.5 block text-xs text-slate-400">{hint}</span> : null}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        onChange={(e) => {
          void take(e.target.files?.[0]);
          // Reset, or picking the same file twice after an error is a no-op.
          e.target.value = "";
        }}
      />

      {value ? (
        <div className="space-y-2">
          <img src={value} alt="" className={previews[shape]} />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={choose}
              disabled={uploading}
            >
              {uploading ? (
                <Spinner className="h-3.5 w-3.5" />
              ) : (
                <UploadIcon className="h-3.5 w-3.5" />
              )}
              {uploading ? "Uploading…" : "Replace"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange("");
                setUploaded(false);
                setError(null);
              }}
              disabled={uploading}
            >
              <XIcon className="h-3.5 w-3.5" />
              Remove
            </Button>
            {uploaded ? <span className="text-xs text-slate-500">Uploaded. {saveHint}</span> : null}
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void take(e.dataTransfer.files[0]);
          }}
          className={cx(
            "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-8 text-center transition-colors",
            dragging ? "border-brand-500 bg-brand-50" : "border-slate-300 bg-white/60",
          )}
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            {uploading ? (
              <Spinner className="h-5 w-5 text-brand-600" />
            ) : (
              <UploadIcon className="h-5 w-5" />
            )}
          </div>
          {uploading ? (
            <p className="text-sm text-slate-500">Uploading…</p>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Drag an image here, or{" "}
                <button
                  type="button"
                  onClick={choose}
                  className="font-medium text-brandText underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                >
                  choose a file
                </button>
              </p>
              <p className="text-xs text-slate-400">
                JPEG, PNG, WebP, or GIF · up to {formatBytes(maxBytes)}
              </p>
            </>
          )}
        </div>
      )}

      {error ? (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}

      <details className="text-sm">
        <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
          Use a link instead
        </summary>
        <div className="mt-2 space-y-1">
          <Input
            type="url"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setUploaded(false);
            }}
            maxLength={2000}
            placeholder={linkPlaceholder}
            aria-label={`${label} URL`}
          />
          <p className="text-xs text-slate-400">
            The address of an image hosted somewhere else. Uploading replaces it.
          </p>
        </div>
      </details>
    </div>
  );
}
